import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  Box,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { 
  Info, 
  Search, 
  ContactPageOutlined as ContactIcon,
  CalendarToday,
  Numbers,
  Badge,
  Person,
} from "@mui/icons-material";
import CustomSelect from "../../components/FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../components/FormFields/CustomInput";
import CustomTextArea from "../../components/CustomForm/CustomTextArea";
import { fundingRoutes, categoriesOfCare } from "../../components/Clients/client.config";
import { CustomDateField } from "../../components/CustomForm";

const ReferenceInformation = ({ handleChange, data }) => {
  const fundingRouteOptions = fundingRoutes.map((item) => ({
    value: item,
    label: item,
  }));

  const careCategoryOptions = categoriesOfCare.map((item) => ({
    value: item,
    label: item,
  }));

  return (
    <Box sx={{ width: "100%", mb: 1 }}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: "#E4E7EC",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <CardHeader
          sx={{
            bgcolor: "#F9FAFB",
            borderBottom: "1px solid #EAECF0",
            py: 0.75,
            px: 2,
          }}
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "6px",
                  bgcolor: "rgba(21, 112, 239, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ContactIcon sx={{ color: "#1570EF", fontSize: 16 }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                  fontSize: "0.85rem",
                  letterSpacing: "0.02em",
                }}
              >
                Reference Information
              </Typography>
            </Box>
          }
        />
        <CardContent sx={{ p: 2 }}>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <CalendarToday sx={{ fontSize: 16, color: '#1570EF' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#344054', fontSize: '0.8rem' }}>
              Service Dates
            </Typography>
          </Box>
          
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={4}>
              <CustomDateField
                label="Service Start Date *"
                name="ref_start_date"
                value={data?.ref_start_date ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CustomDateField
                label="Service End Date"
                name="ref_end_date"
                value={data?.ref_end_date ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CustomDateField
                label="Date of Death"
                name="ref_date_of_death"
                value={data?.ref_date_of_death ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Badge sx={{ fontSize: 16, color: '#1570EF' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#344054', fontSize: '0.8rem' }}>
              Basic Information
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={4}>
              <CustomInput
                label="Service User ID *"
                name="service_user"
                value={data?.service_user ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., SU-2024-0042"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CustomSelect
                options={fundingRouteOptions}
                name="ref_funding_route"
                value={data?.ref_funding_route ?? ""}
                onChange={handleChange}
                placeholder="e.g., Local Authority"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CustomSelect
                name="ref_category_of_care"
                options={careCategoryOptions}
                value={data?.ref_category_of_care ?? ""}
                onChange={handleChange}
                placeholder="e.g., Domiciliary Care"
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Numbers sx={{ fontSize: 16, color: '#1570EF' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#344054', fontSize: '0.8rem' }}>
              Reference Numbers
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="NI Number"
                name="ni_number"
                value={data?.ni_number ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., AB123456C"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="Order Number"
                name="order_number"
                value={data?.order_number ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., ORD-2024-789"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="AS Number"
                name="as_number"
                value={data?.as_number ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., AS-4567-UK"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="Case Number"
                name="case_number"
                value={data?.case_number ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., C-2024-12345"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="SPROC Number"
                name="sproc_number"
                value={data?.sproc_number ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., SP-9876-UK"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="LAS Number"
                name="las_number"
                value={data?.las_number ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., LAS-2024-567"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Person sx={{ fontSize: 16, color: '#1570EF' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#344054', fontSize: '0.8rem' }}>
              Staff & Preferences
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="Key Worker"
                name="ref_key_worker"
                value={data?.ref_key_worker ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., Sarah Johnson"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search fontSize="small" sx={{ color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomInput
                label="Named Nurse"
                name="ref_named_nurse"
                value={data?.ref_named_nurse ?? ""}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g., Dr. Emily Patel"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search fontSize="small" sx={{ color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                  <CustomTextArea
                    label="Change Preference"
                    name="area_preference"
                    value={data?.area_preference ?? ""}
                    onChange={handleChange}
                    rows={3}
                    fullWidth
                    size="small"
                    placeholder="e.g., Prefers Birmingham area, near family"
                  />
                </Box>
                <Tooltip title="Client's preference for care location or area">
                  <IconButton 
                    color="primary" 
                    sx={{ 
                      mt: 3.5, 
                      p: 0.5,
                      bgcolor: 'rgba(21, 112, 239, 0.06)',
                      '&:hover': { bgcolor: 'rgba(21, 112, 239, 0.12)' }
                    }}
                  >
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

        </CardContent>
      </Card>
    </Box>
  );
};

export default ReferenceInformation;